import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Clock, History, CalendarClock, CalendarPlus, UserCheck, User, Check,
    Archive, RotateCcw, Eye, ChevronDown, ChevronsDownUp, ChevronsUpDown, Pencil, Info, CheckCircle2, X,
} from 'lucide-react';
import Modal from '../../../components/Modal';
import { Skeleton } from '../../../skeletons';
import { Select, Button } from '../../../components';
import careNeedsService from '../../../services/carehome_manager/careNeedsService';
import CarePlanEditor, { blankDraft, draftFromPlan } from './CarePlanEditor';
import {
    CARE_PLAN_CATEGORIES, categoryById, categoryLabel, STATUS_LABEL, statusBadgeClass,
    priorityBadgeClass, ARCHIVABLE, NEEDS_REVIEW_DATE, HISTORY_KIND_LABEL,
    formatDate, todayISO, splitActions,
} from './carePlanConstants';

/**
 * Care plans for one resident.
 *
 * Plans are grouped by category (left rail, with counts) and a category can
 * hold several titled plans, stacked one under another — each one expands and
 * minimises on its own, with Expand all / Collapse all. Current and Archived are separate
 * views. Editing, approval and Patch drafting happen in CarePlanEditor; every
 * approval, archive and restore is versioned server-side and each version can
 * be opened read-only from the review history.
 */

const initialsOf = (name) =>
    (name || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'U';

const isArchived = (p) => !!p.details?.archived;

const isOverdue = (p) =>
    !isArchived(p) && NEEDS_REVIEW_DATE.includes(p.status) && !!p.details?.nextReviewDate
    && String(p.details.nextReviewDate).slice(0, 10) < todayISO();

const daysSince = (date) => Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000));

const Badge = ({ className, children }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${className}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {children}
    </span>
);

const Notice = ({ tone = 'info', children }) => {
    const tones = {
        info: 'bg-blue-50 border-blue-100 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800/40 dark:text-blue-200',
        warn: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/40 dark:text-amber-300',
        muted: 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-800/40 dark:border-gray-700 dark:text-gray-300',
    };
    return <div className={`mb-6 rounded-xl border px-4 py-3 text-[13px] leading-relaxed ${tones[tone]}`}>{children}</div>;
};

const Section = ({ title, children }) => (
    <section className="mb-6">
        <h4 className="text-[14px] font-semibold text-blue-600 dark:text-blue-400 mb-2">{title}</h4>
        {children}
    </section>
);

const MetaItem = ({ icon: Icon, label, children }) => (
    <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1.5">
            <Icon className="w-3.5 h-3.5" /> {label}
        </p>
        <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 break-words">{children}</div>
    </div>
);

// `focusId` (optional) — a care plan to select once loaded; set by a
// reminder link (?focus=), see utils/reminderLinks.js.
const CareNeedsTab = ({ residenceId, residentName = '', focusId = null }) => {
    const [plans, setPlans] = useState([]);
    const [reviewers, setReviewers] = useState([]); // "Responsible for review" choices
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageMessage, setPageMessage] = useState('');

    const [archiveView, setArchiveView] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    // Which stacked plans are expanded. null = the default for a freshly opened
    // category: only the first plan open. Toggling turns it into an explicit list.
    const [openIds, setOpenIds] = useState(null);
    const [scrollToId, setScrollToId] = useState(null);

    const [editorDrafts, setEditorDrafts] = useState(null); // null = closed

    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveBusy, setArchiveBusy] = useState(false);
    const [archiveError, setArchiveError] = useState('');

    const [historyView, setHistoryView] = useState(null); // { plan, entry }

    // ── Data ─────────────────────────────────────────────────────────────────

    const loadPlans = useCallback(async () => {
        const res = await careNeedsService.getCareNeedsByResidence(residenceId);
        const list = res.data || [];
        setPlans(list);
        return list;
    }, [residenceId]);

    useEffect(() => {
        if (!residenceId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                // The review team is a nicety for the editor: if it can't load
                // (e.g. an API that predates it) the plans must still show.
                const [list, reviewersRes] = await Promise.all([
                    loadPlans(),
                    careNeedsService.getReviewers(residenceId).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setReviewers(reviewersRes.data || []);
                const first = list.find((p) => !isArchived(p));
                setSelectedCategory(first?.category_id || CARE_PLAN_CATEGORIES[0].id);
                setOpenIds(null);
                setError(null);
            } catch (err) {
                console.error('Error fetching care plans:', err);
                if (!cancelled) setError('Failed to load care plans');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [residenceId, loadPlans]);

    // A reminder link points at one plan: open its category and view.
    useEffect(() => {
        if (!focusId) return;
        const hit = plans.find((p) => String(p.id) === String(focusId));
        if (!hit) return;
        setArchiveView(isArchived(hit));
        setSelectedCategory(hit.category_id);
        focusPlan(hit.id);
    }, [focusId, plans]);

    // Bring a plan opened by a link, a save or a restore into view once it renders.
    useEffect(() => {
        if (!scrollToId) return;
        const frame = requestAnimationFrame(() => {
            document.getElementById(`care-plan-${scrollToId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setScrollToId(null);
        });
        return () => cancelAnimationFrame(frame);
    }, [scrollToId, plans, selectedCategory, archiveView]);

    // ── Derived ──────────────────────────────────────────────────────────────

    const inView = useMemo(() => plans.filter((p) => isArchived(p) === archiveView), [plans, archiveView]);
    const countFor = (catId) => inView.filter((p) => p.category_id === catId).length;
    const category = categoryById(selectedCategory) || CARE_PLAN_CATEGORIES[0];
    const matches = inView.filter((p) => p.category_id === category.id);

    const openList = openIds ?? (matches[0] ? [matches[0].id] : []);
    const isPlanOpen = (id) => openList.includes(id);
    const togglePlan = (id) => setOpenIds(isPlanOpen(id) ? openList.filter((x) => x !== id) : [...openList, id]);
    const allOpen = matches.length > 0 && matches.every((p) => isPlanOpen(p.id));
    const setAllOpen = (open) => setOpenIds(open ? matches.map((p) => p.id) : []);

    /** Open one plan (and only that one) and scroll to it. */
    function focusPlan(id) {
        setOpenIds([id]);
        setScrollToId(id);
    }

    const selectCategory = (id) => { setSelectedCategory(id); setOpenIds(null); };
    const switchView = (archived) => { setArchiveView(archived); setOpenIds(null); };

    // ── Editor ───────────────────────────────────────────────────────────────

    const openCreate = (categoryId = '') => setEditorDrafts([blankDraft(categoryId)]);
    const openEdit = (p) => setEditorDrafts([draftFromPlan(p)]);

    const handleSaved = async (saved, { approved, discarded } = {}) => {
        try {
            await loadPlans();
            if (saved) {
                setArchiveView(false);
                setSelectedCategory(saved.category_id);
                focusPlan(saved.id);
            }
            if (approved && saved) setPageMessage(`“${saved.title}” approved. Carers will see the updated plan in the app.`);
            if (discarded) setPageMessage('Draft discarded.');
        } catch (err) {
            console.error('Error refreshing care plans:', err);
        }
    };

    // ── Archive / restore ────────────────────────────────────────────────────

    const openArchive = (p) => {
        setArchiveTarget(p);
        setArchiveReason('');
        setArchiveError('');
    };

    const submitArchive = async (e) => {
        e.preventDefault();
        const reason = archiveReason.trim();
        if (!reason) { setArchiveError('Enter a reason.'); return; }
        const restoring = isArchived(archiveTarget);
        setArchiveBusy(true);
        try {
            const body = { reason, baseVersion: archiveTarget.details?.version || 0 };
            const res = restoring
                ? await careNeedsService.restoreCareNeed(residenceId, archiveTarget.id, body)
                : await careNeedsService.archiveCareNeed(residenceId, archiveTarget.id, body);
            await loadPlans();
            setArchiveView(!restoring);
            setSelectedCategory(archiveTarget.category_id);
            focusPlan(archiveTarget.id);
            setPageMessage(restoring
                ? `“${archiveTarget.title}” restored with its ${STATUS_LABEL[res?.data?.status || archiveTarget.status]} status. History retained.`
                : `“${archiveTarget.title}” archived. The plan and its review history are retained.`);
            setArchiveTarget(null);
        } catch (err) {
            setArchiveError(err?.response?.data?.message || 'Could not update this plan.');
        } finally {
            setArchiveBusy(false);
        }
    };

    // ── Render pieces ────────────────────────────────────────────────────────

    const renderHistory = (p) => {
        const entries = Array.isArray(p.details?.reviews) ? [...p.details.reviews] : [];
        // Plans reviewed before the history array existed kept one review inline.
        if (!entries.length && p.details?.lastReviewedAt) {
            entries.push({ id: 'legacy', kind: p.details.reviewOutcome || 'updated', notes: p.details.reviewNotes, date: p.details.lastReviewedAt, by: p.details.reviewerName });
        }
        entries.sort((a, b) => new Date(b.date) - new Date(a.date));
        return (
            <details className="group mt-6 border-t border-gray-100 dark:border-gray-800" open={entries.length > 0 && entries.length <= 3}>
                <summary className="flex items-center gap-2 py-4 cursor-pointer list-none">
                    <History className="w-4 h-4 text-indigo-500" />
                    <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-100">Review history</span>
                    <span className="text-[13px] text-gray-400">({entries.length})</span>
                    <ChevronDown className="w-4 h-4 text-gray-400 ml-auto transition-transform group-open:rotate-180" />
                </summary>
                {entries.length === 0 ? (
                    <p className="text-[12.5px] text-gray-500 dark:text-gray-400 pb-2">No approved review yet.</p>
                ) : entries.map((h, i) => (
                    <div key={h.id || i} className="py-3.5 border-b last:border-b-0 border-gray-100 dark:border-gray-800">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                                {HISTORY_KIND_LABEL[h.kind || h.outcome] || 'Updated'} · {formatDate(h.date)}
                                {h.patchAssisted && <span className="ml-2 text-[11px] font-medium text-blue-600 dark:text-blue-400">Patch-assisted</span>}
                            </p>
                            {h.snapshot && (
                                <Button size="sm" variant="secondary" leftIcon={<Eye className="w-3.5 h-3.5" />} onClick={() => setHistoryView({ plan: p, entry: h })}>
                                    View version {h.snapshot.version || h.version}
                                </Button>
                            )}
                        </div>
                        <p className="mt-1 text-[12.5px] text-gray-600 dark:text-gray-300 leading-relaxed">
                            {[h.by, h.notes].filter(Boolean).join(' · ')}
                        </p>
                        {Array.isArray(h.changes) && h.changes.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {h.changes.map((c, ci) => (
                                    <span key={ci} className="text-[10.5px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-md">
                                        {/* History saved before the rename still says "Responsible person". */}
                                        {c.field === 'Responsible person' ? 'Responsible for review' : c.field}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </details>
        );
    };

    const renderPlans = () => {
        const Icon = category.icon;
        if (!matches.length) {
            return (
                <div className="text-center px-6 py-14">
                    <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                        <Icon className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[15px] font-semibold text-gray-800 dark:text-gray-100">No {archiveView ? 'archived' : 'current'} care plans in this category</h3>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1 mb-5">
                        {archiveView ? 'Archived plans retain their content and history.' : 'Create a plan yourself or let Patch organise your notes into draft plans.'}
                    </p>
                    {!archiveView && <Button onClick={() => openCreate(category.id)}>Create care plan</Button>}
                </div>
            );
        }

        return (
            <div className="p-4 sm:p-5 space-y-3">
                {matches.map((p) => renderPlanCard(p))}
            </div>
        );
    };

    /** One stacked plan: a header that expands / minimises it, and its body. */
    const renderPlanCard = (plan) => {
        const d = plan.details || {};
        const archived = isArchived(plan);
        const isDraft = plan.status === 'draft';
        const hasPendingDraft = !isDraft && !!d.draft;
        const overdue = isOverdue(plan);
        const open = isPlanOpen(plan.id);
        const bodyId = `care-plan-body-${plan.id}`;

        return (
            <article key={plan.id} id={`care-plan-${plan.id}`}
                className={`rounded-xl border scroll-mt-4 transition-colors ${open
                    ? 'border-blue-200 dark:border-blue-900/60'
                    : 'border-gray-200 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-900/60'}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3.5">
                    <button type="button" onClick={() => togglePlan(plan.id)} aria-expanded={open} aria-controls={bodyId}
                        className="flex flex-1 min-w-[220px] items-start gap-3 text-left">
                        <ChevronDown className={`w-4 h-4 mt-1 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                        <span className="min-w-0 flex-1">
                            <span className="block text-[16px] font-semibold text-gray-800 dark:text-gray-100 break-words">{plan.title || 'Untitled draft'}</span>
                            <span className="mt-1.5 flex flex-wrap items-center gap-2">
                                {!isDraft && plan.priority && <Badge className={priorityBadgeClass[plan.priority]}><span className="capitalize">{plan.priority}</span> priority</Badge>}
                                <Badge className={statusBadgeClass[plan.status]}>{STATUS_LABEL[plan.status] || plan.status}</Badge>
                                {archived && <Badge className={statusBadgeClass.archived}>Archived</Badge>}
                                {hasPendingDraft && <Badge className={statusBadgeClass.draft}>Unapproved changes</Badge>}
                                {overdue && <Badge className="bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400">Review overdue</Badge>}
                                <span className="text-[11.5px] text-gray-400 dark:text-gray-500 font-medium">
                                    {isDraft ? 'Not visible to carers' : `Last updated: ${formatDate(d.lastReviewedAt || plan.updated_at)}`}
                                </span>
                            </span>
                        </span>
                    </button>
                    {renderPlanActions(plan)}
                </div>
                {open && (
                    <div id={bodyId} className="px-4 sm:px-5 pt-5 pb-1 border-t border-gray-100 dark:border-gray-800">
                        {renderPlanBody(plan)}
                    </div>
                )}
            </article>
        );
    };

    const renderPlanBody = (plan) => {
        const d = plan.details || {};
        const archived = isArchived(plan);
        const isDraft = plan.status === 'draft';
        const hasPendingDraft = !isDraft && !!d.draft;
        const overdue = isOverdue(plan);
        const actions = splitActions(d.actions);
        const reviewsLogged = Array.isArray(d.reviews) ? d.reviews.length : (d.lastReviewedAt ? 1 : 0);

        return (
            <>
                {archived ? (
                    <Notice tone="muted">Archived · retained for reference. Restoring keeps its previous status; it does not reactivate the plan.</Notice>
                ) : isDraft ? (
                    <Notice>Saved draft{d.draft?.savedBy ? ` by ${d.draft.savedBy}` : ''}{d.draft?.savedAt ? ` · ${formatDate(d.draft.savedAt)}` : ''}. Not visible to carers until it is approved.</Notice>
                ) : overdue ? (
                    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                                <Clock className="w-4 h-4 text-red-500" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[13px] font-semibold text-red-700 dark:text-red-400">
                                    Review overdue by {daysSince(d.nextReviewDate)} {daysSince(d.nextReviewDate) === 1 ? 'day' : 'days'}
                                </p>
                                <p className="text-[12px] text-red-500">Scheduled for {formatDate(d.nextReviewDate)}</p>
                            </div>
                        </div>
                        <Button size="sm" variant="danger" leftIcon={<Check className="w-3.5 h-3.5" />} onClick={() => openEdit(plan)}>Review now</Button>
                    </div>
                ) : plan.status === 'on_hold' ? (
                    <Notice tone="warn">On hold · temporarily not being followed. Review due {formatDate(d.nextReviewDate)}. Edit and approve as Active to resume.</Notice>
                ) : ARCHIVABLE.includes(plan.status) ? (
                    <Notice tone="muted">{plan.status === 'achieved' ? 'Goal completed.' : 'Plan discontinued.'} Retained for reference; archive when ready.</Notice>
                ) : null}

                {hasPendingDraft && !archived && (
                    <Notice>
                        Unapproved changes saved{d.draft.savedBy ? ` by ${d.draft.savedBy}` : ''} on {formatDate(d.draft.savedAt)}. Carers still see the approved version below until the changes are approved.
                    </Notice>
                )}

                <Section title="Current situation">
                    <p className="text-[13.5px] text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">{(isDraft ? d.draft?.currentSituation : d.currentSituation) || 'Not entered'}</p>
                </Section>
                <Section title="Expected outcome">
                    <p className="text-[13.5px] text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">{(isDraft ? d.draft?.expectedOutcome : d.expectedOutcome) || 'Not entered'}</p>
                </Section>
                <Section title="Care actions">
                    {(() => {
                        const list = isDraft ? splitActions(d.draft?.actions) : actions;
                        return list.length ? (
                            <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-gray-700 dark:text-gray-300 leading-relaxed">
                                {list.map((a, i) => <li key={i} className="break-words">{a}</li>)}
                            </ul>
                        ) : <p className="text-[13.5px] text-gray-400">Not entered</p>;
                    })()}
                </Section>

                <div className="bg-[#F8FAFC] dark:bg-[#1b1f26] border border-gray-100 dark:border-gray-800 rounded-xl px-5 py-4 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    <MetaItem icon={User} label="Responsible for review">
                        {plan.responsiblePerson?.name ? (
                            <span className="inline-flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                    {initialsOf(plan.responsiblePerson.name)}
                                </span>
                                {plan.responsiblePerson.name}
                            </span>
                        ) : <span className="text-gray-400">Unassigned</span>}
                    </MetaItem>
                    <MetaItem icon={Clock} label="Last reviewed">{isDraft ? 'Not yet reviewed' : formatDate(d.lastReviewedAt)}</MetaItem>
                    <MetaItem icon={CalendarClock} label="Next review">
                        <span className={overdue ? 'text-red-600 dark:text-red-400' : ''}>{formatDate(isDraft ? d.draft?.nextReviewDate : d.nextReviewDate)}</span>
                    </MetaItem>
                    <MetaItem icon={UserCheck} label="Created by">{plan.creator?.name || '—'}</MetaItem>
                    <MetaItem icon={CalendarPlus} label="Created date">{formatDate(plan.created_at)}</MetaItem>
                    <MetaItem icon={History} label="Reviews logged">{reviewsLogged}</MetaItem>
                </div>

                {renderHistory(plan)}
            </>
        );
    };

    const renderPlanActions = (plan) => {
        if (isArchived(plan)) {
            return <Button size="sm" variant="secondary" leftIcon={<RotateCcw className="w-3.5 h-3.5" />} onClick={() => openArchive(plan)}>Restore</Button>;
        }
        const isDraft = plan.status === 'draft';
        return (
            <div className="flex flex-wrap gap-2">
                <Button size="sm" leftIcon={<Pencil className="w-3.5 h-3.5" />} onClick={() => openEdit(plan)}>
                    {isDraft || plan.details?.draft ? 'Continue draft' : 'Edit / change status'}
                </Button>
                {ARCHIVABLE.includes(plan.status) && (
                    <Button size="sm" variant="secondary" leftIcon={<Archive className="w-3.5 h-3.5" />} onClick={() => openArchive(plan)}>Archive</Button>
                )}
            </div>
        );
    };

    const historySnapshot = historyView?.entry?.snapshot;
    const ActiveIcon = category.icon;

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="mt-6 animate-in fade-in duration-300">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Care plans</h2>
                    {residentName && <p className="text-[12.5px] text-gray-500 dark:text-gray-400">{residentName}</p>}
                </div>
                <Button onClick={() => openCreate()}>Create new care plan</Button>
            </div>

            {pageMessage && (
                <div role="status" className="mb-4 flex items-start gap-2.5 rounded-xl border border-emerald-100 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-[13px] text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="flex-1">{pageMessage}</span>
                    <button type="button" onClick={() => setPageMessage('')} aria-label="Dismiss" className="text-emerald-600/70 hover:text-emerald-800"><X className="w-4 h-4" /></button>
                </div>
            )}
            {error && (
                <div className="mb-4 rounded-xl border border-red-100 bg-red-50 dark:bg-red-900/20 dark:border-red-800/40 px-4 py-3 text-[13px] text-red-700 dark:text-red-400">{error}</div>
            )}

            {/* Current / Archived */}
            <div className="inline-flex p-1 mb-5 rounded-xl bg-gray-100 dark:bg-[#1b1f26]" role="group" aria-label="Plan collection">
                {[[false, 'Current plans'], [true, 'Archived plans']].map(([value, label]) => (
                    <button key={label} type="button" aria-pressed={archiveView === value} onClick={() => switchView(value)}
                        className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition ${archiveView === value
                            ? 'bg-white dark:bg-[#262b33] text-blue-600 dark:text-blue-400 shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}>
                        {label}
                        <span className="ml-1.5 text-[11.5px] text-gray-400">{plans.filter((p) => isArchived(p) === value).length}</span>
                    </button>
                ))}
            </div>

            {/* Category picker on narrow screens */}
            <div className="md:hidden mb-4">
                <label className="block text-[12.5px] text-gray-500 dark:text-gray-400 mb-1.5">Care plan category</label>
                <Select value={category.id} onChange={selectCategory} className="w-full"
                    options={CARE_PLAN_CATEGORIES.map((c) => {
                        const n = countFor(c.id);
                        return { value: c.id, label: `${c.label} — ${n ? `${n} plan${n === 1 ? '' : 's'}` : 'No plans'}` };
                    })} />
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Category rail */}
                <nav className="hidden md:flex w-[250px] lg:w-[280px] flex-shrink-0 flex-col gap-2" aria-label="Care plan categories">
                    {loading ? [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />) : CARE_PLAN_CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const n = countFor(c.id);
                        const active = c.id === category.id;
                        const due = inView.some((p) => p.category_id === c.id && isOverdue(p));
                        return (
                            <button key={c.id} type="button" onClick={() => selectCategory(c.id)} aria-pressed={active}
                                aria-label={`${c.label} — ${n ? `${n} plan${n === 1 ? '' : 's'}` : 'No plans'}`}
                                className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-left border transition ${active
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                    : n
                                        ? 'bg-[#EEF4FF] dark:bg-blue-900/20 border-[#C4D6FF] dark:border-blue-900/50 text-gray-800 dark:text-gray-100 font-semibold hover:border-blue-300'
                                        : 'bg-[#F8FAFC] dark:bg-[#262b33] border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-200 dark:hover:border-gray-700'}`}>
                                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : n ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} strokeWidth={1.6} />
                                <span className="flex-1 min-w-0 text-[13.5px]">{c.label}</span>
                                {due && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-white' : 'bg-red-500'}`} title="Review overdue" />}
                                {n > 0 && (
                                    <span className={`min-w-[26px] h-[26px] px-1.5 rounded-full flex items-center justify-center text-[12.5px] font-bold flex-shrink-0 ${active ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'}`}>{n}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Category card — its plans stacked, each expanding / minimising */}
                <div className="flex-1 min-w-0 bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm h-fit">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-3 min-w-0">
                            <ActiveIcon className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0" strokeWidth={1.5} />
                            <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 truncate">{category.label}</h3>
                            {!loading && matches.length > 0 && (
                                <span className="text-[12.5px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                                    {matches.length} plan{matches.length === 1 ? '' : 's'}
                                </span>
                            )}
                        </div>
                        {!loading && matches.length > 1 && (
                            <Button size="sm" variant="secondary" onClick={() => setAllOpen(!allOpen)}
                                leftIcon={allOpen ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}>
                                {allOpen ? 'Collapse all' : 'Expand all'}
                            </Button>
                        )}
                    </div>
                    {loading ? <div className="p-6"><Skeleton className="h-40 w-full rounded-xl" /></div> : renderPlans()}
                </div>
            </div>

            {/* Editor */}
            <CarePlanEditor
                isOpen={!!editorDrafts}
                initialDrafts={editorDrafts}
                onClose={() => setEditorDrafts(null)}
                residenceId={residenceId}
                residentName={residentName}
                reviewers={reviewers}
                onSaved={handleSaved}
            />

            {/* Archive / restore */}
            <Modal isOpen={!!archiveTarget} onClose={() => setArchiveTarget(null)}
                title={archiveTarget && isArchived(archiveTarget) ? 'Restore care plan' : 'Archive care plan'} maxWidth="max-w-[500px]">
                {archiveTarget && (
                    <form onSubmit={submitArchive} className="space-y-4">
                        <div className="flex gap-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 px-4 py-3 text-[13px] text-blue-800 dark:text-blue-200">
                            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                                <strong>{archiveTarget.title}</strong> · {isArchived(archiveTarget)
                                    ? `Restore to Current plans with its existing ${STATUS_LABEL[archiveTarget.status]} status. This does not make it Active.`
                                    : 'Move to Archived plans. The plan and its review history will be retained.'}
                            </span>
                        </div>
                        <div>
                            <label htmlFor="archive-reason" className="block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Reason <span className="text-red-500">*</span></label>
                            <textarea id="archive-reason" rows={3} value={archiveReason} onChange={(e) => { setArchiveReason(e.target.value); setArchiveError(''); }}
                                className="w-full bg-white dark:bg-[#1f2329] border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 text-[13.5px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y" />
                            <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">Added to the review history.</p>
                        </div>
                        {archiveError && <p role="status" className="text-[12.5px] text-red-600 dark:text-red-400">{archiveError}</p>}
                        <div className="flex justify-end gap-2 pt-1">
                            <Button variant="secondary" onClick={() => setArchiveTarget(null)}>Cancel</Button>
                            <Button type="submit" loading={archiveBusy}>{isArchived(archiveTarget) ? 'Restore plan' : 'Archive plan'}</Button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Read-only past version */}
            <Modal isOpen={!!historyView} onClose={() => setHistoryView(null)} maxWidth="max-w-[640px]"
                title={historySnapshot ? `Version ${historySnapshot.version} · ${historySnapshot.title}` : 'Care plan version'}>
                {historyView && historySnapshot && (
                    <div>
                        <Notice tone="warn">Historical record — read only. This shows the plan as saved at this event, not necessarily the current care instructions.</Notice>
                        <div className="flex flex-wrap gap-2 mb-5">
                            <Badge className={statusBadgeClass[historySnapshot.status]}>{STATUS_LABEL[historySnapshot.status] || historySnapshot.status}</Badge>
                            {historySnapshot.archived && <Badge className={statusBadgeClass.archived}>Archived</Badge>}
                            {historySnapshot.priority && <Badge className={priorityBadgeClass[historySnapshot.priority]}><span className="capitalize">{historySnapshot.priority}</span> priority</Badge>}
                        </div>
                        <Section title="Review / event">
                            <p className="text-[13.5px] text-gray-700 dark:text-gray-300">
                                {HISTORY_KIND_LABEL[historyView.entry.kind] || 'Updated'} · {formatDate(historyView.entry.date)}{historyView.entry.by ? ` · ${historyView.entry.by}` : ''}
                            </p>
                        </Section>
                        <Section title="Reason / review summary">
                            <p className="text-[13.5px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{historyView.entry.notes || 'Not recorded'}</p>
                        </Section>
                        <Section title="Category"><p className="text-[13.5px] text-gray-700 dark:text-gray-300">{categoryLabel(historySnapshot.category)}</p></Section>
                        <Section title="Current situation"><p className="text-[13.5px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{historySnapshot.currentSituation || 'Not recorded'}</p></Section>
                        <Section title="Expected outcome"><p className="text-[13.5px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{historySnapshot.expectedOutcome || 'Not recorded'}</p></Section>
                        <Section title="Care actions">
                            <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-gray-700 dark:text-gray-300">
                                {splitActions(historySnapshot.actions).map((a, i) => <li key={i} className="break-words">{a}</li>)}
                            </ul>
                        </Section>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[13px]">
                            <MetaItem icon={User} label="Responsible for review">{historySnapshot.responsiblePersonName || 'Unassigned'}</MetaItem>
                            <MetaItem icon={Clock} label="Last reviewed">{formatDate(historySnapshot.lastReviewedAt)}</MetaItem>
                            <MetaItem icon={CalendarClock} label="Next review">{formatDate(historySnapshot.nextReviewDate)}</MetaItem>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CareNeedsTab;
